<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">

      <!-- Header -->
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Correct Form Compliance Tag</h2>
        <p class="text-sm text-gray-500 mt-1">
          Use this form to manually update the continuous filing compliance status for a company.
          Select the specific form and indicate whether the company is currently compliant.
        </p>
      </div>

      <!-- Card -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <!-- Error Banner -->
        <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md flex items-start gap-2">
          <i class="fa fa-exclamation-circle mt-0.5 text-red-500"></i>
          <span>{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="onSubmit" class="space-y-4">

          <!-- BRN -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Business Registration
              Number</label>
            <input type="text" v-model="form.brn" placeholder="e.g. 80000012345678" required
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
          </div>

          <!-- Rationale -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Rationale</label>
            <textarea v-model="form.rationale" placeholder="Briefly explain the reason for this correction..." required
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 h-24 resize-none"></textarea>
          </div>

          <!-- Target form + compliance -->
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Target Form</label>
              <select v-model="form.target"
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
                <option :value="null">-- Select Form --</option>
                <option value="FORM_21">Form 21</option>
                <option value="FORM_23">Form 23</option>
                <option value="FORM_28">Form 28</option>
                <option value="FORM_29">Form 29</option>
                <option value="FORM_30">Form 30</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Is Compliant?</label>
              <select v-model="form.compliant"
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
                <option :value="null">-- Select --</option>
                <option :value="true">Yes — Compliant</option>
                <option :value="false">No — Not Compliant</option>
              </select>
            </div>
          </div>

          <!-- Divider -->
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-check mr-1.5"></i> Apply Changes
            </button>
          </div>

        </form>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useInternalProcessesStore } from "../stores";
import { useToast } from "@/composables";

const store = useInternalProcessesStore();
const { successToast, errorToast } = useToast();

const loading = ref(false);
const errorMessage = ref<string | null>(null);

const form = ref({
  brn: null as string | null,
  rationale: null as string | null,
  compliant: false as boolean | null,
  target: null as string | null
});

const resetForm = () => {
  form.value = { brn: null, rationale: null, compliant: false, target: null };
};

const onSubmit = () => {
  loading.value = true;
  errorMessage.value = null;
  store.updateFormCompliance(form.value);
};

watch(() => store.formComplianceStatusChange, (data) => {
  if (data) {
    loading.value = false;
    errorMessage.value = null;
    successToast(data.message || "Form compliance status updated successfully.");
    resetForm();
  }
});

watch(() => store.formComplianceStatusError, (err) => {
  loading.value = false;
  if (err) {
    const msg = err.message || err.error || "An error occurred while updating form compliance.";
    errorMessage.value = msg;
    errorToast(msg);
  }
});
</script>
